import React from 'react';
import { Shield, CheckCircle, Globe, Package, Target } from 'lucide-react';

export default function TariffEngineering() {
  const framework = [
    {
      phase: 'Phase 1: Classification & Risk Audit',
      duration: 'Weeks 1-2',
      activities: [
        'HS code validation with advance rulings and binding opinions referenced',
        'Duty impact heat map across current and planned product assortment',
        'Risk scoring for forced labor, ESG, and country-of-origin exposure',
        'Baseline landed cost model capturing duty, VAT, and brokerage fees'
      ]
    },
    {
      phase: 'Phase 2: Design & Scenario Modeling',
      duration: 'Weeks 3-5',
      activities: [
        'Tariff engineering concepts including material swaps and modular kits',
        'USMCA / CUSMA qualification and certification roadmap',
        'Nearshoring options with comparative cost and lead time analysis',
        'De Minimis and Section 321 strategies for DTC parcel flows'
      ]
    },
    {
      phase: 'Phase 3: Implementation Toolkit',
      duration: 'Weeks 6-10',
      activities: [
        'Bill of material reconfiguration and specification updates',
        'Supplier negotiations covering pricing, compliance, and documentation',
        'Customs broker briefings and SOP alignment',
        'Digital traceability setup for proofs of origin and audit trails'
      ]
    },
    {
      phase: 'Phase 4: Compliance & Monitoring',
      duration: 'Ongoing',
      activities: [
        'Quarterly tariff change watchlist and alerting system',
        'Randomized entry audits with corrective action tracking',
        'Drawback and refund program administration',
        'Annual refresher training for operations and finance teams'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'Landed Cost Intelligence',
      icon: Target,
      description: 'We quantify savings scenarios down to unit economics so executives can greenlight changes with confidence.'
    },
    {
      title: 'Trade Partnership Network',
      icon: Globe,
      description: 'Access vetted customs attorneys, broker partners, and compliance auditors in every key region.'
    },
    {
      title: 'Product Engineering Collaboration',
      icon: Package,
      description: 'We work hands-on with product and sourcing teams to protect brand experience while reclassifying intelligently.'
    },
    {
      title: 'Audit-Proof Governance',
      icon: Shield,
      description: 'Documentation, SOPs, and digital repositories keep you compliant even as regulations shift.'
    }
  ];

  const deliverables = [
    'Tariff engineering playbook with prioritized initiatives and savings bands',
    'Updated HS code matrix with origin documentation checklist',
    'Implementation roadmap covering suppliers, operations, and legal owners',
    'Monitoring dashboard concepts with trigger events and response playbooks'
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">Tariff Engineering & Trade Strategy</h1>
              <p className="text-xl text-gray-600 mt-2">Protect margins with compliant, future-ready supply chains</p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Trade Advantage Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Tariffs, de minimis thresholds, and trade agreements change constantly. We engineer product and supply strategies that keep landed costs predictable while satisfying every customs checkpoint.
          </p>
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Why It Works</h2>
          <p className="text-lg text-gray-700 mb-8">
            Cost savings mean nothing if customs flags a shipment. Our approach combines strategic creativity with airtight compliance.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">Deliverables You Can Operate</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Engineer Duty Savings?</h2>
          <p className="text-xl mb-8 opacity-90">
            We align product, sourcing, and compliance teams around one objective—keep goods flowing while defending margins.
          </p>
          <a
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
